import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import DraggableDialog from 'components/common/DraggableDialog';
import { ActionButtonGroup } from 'components/forms';
import { HiGridComponent, useHiGridHook, hiCol } from 'components/grid/hiGrid';
import { theme } from 'styles/theme';

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  height: 100%;
  min-height: 0;
`;

const TabBar = styled.div`
  display: flex;
  gap: 4px;
  border-bottom: 1px solid ${theme.colors.border || '#e5e7eb'};
`;

const TabBtn = styled.button`
  appearance: none;
  border: 1px solid transparent;
  border-bottom: none;
  background: ${(p) => (p.$active ? 'var(--erp-color-bg-white, #fff)' : 'transparent')};
  color: ${(p) =>
    p.$active ? `var(--erp-color-primary, ${theme.colors.primary})` : theme.colors.text?.secondary || '#6b7280'};
  font-size: 0.8125rem;
  font-weight: ${(p) => (p.$active ? 600 : 500)};
  padding: 6px 12px;
  cursor: pointer;
  border-radius: 4px 4px 0 0;
  margin-bottom: -1px;
  border-color: ${(p) => (p.$active ? theme.colors.border || '#e5e7eb' : 'transparent')};
  border-bottom-color: ${(p) => (p.$active ? 'var(--erp-color-bg-white, #fff)' : 'transparent')};
`;

const SearchBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 12px;
  flex-shrink: 0;
`;

const SearchField = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 0.8125rem;
  color: ${theme.colors.text?.primary || '#111827'};
  white-space: nowrap;
`;

const SearchInput = styled.input`
  width: ${(p) => p.$width || '140px'};
  min-height: 26px;
  padding: 3px 8px;
  font-size: 0.8125rem;
  border: 1px solid ${theme.colors.border || '#d1d5db'};
  border-radius: 4px;
  box-sizing: border-box;
  &:focus {
    outline: none;
    border-color: var(--erp-color-primary, ${theme.colors.primary});
  }
`;

const GridWrap = styled.div`
  flex: 1;
  min-height: 280px;
  display: flex;
  flex-direction: column;
`;

const Hint = styled.div`
  font-size: 0.75rem;
  color: ${theme.colors.text?.secondary || '#6b7280'};
`;

function includesIgnoreCase(hay, needle) {
  if (!needle) return true;
  return String(hay ?? '')
    .toLowerCase()
    .includes(String(needle).toLowerCase().trim());
}

function withLookupTab(row, hasTabs, activeTabId) {
  if (!row) return row;
  return hasTabs ? { ...row, _lookupTab: activeTabId } : row;
}

/**
 * 코드 조회 공통 팝업
 * @param {boolean} [multiSelect] - true면 행 멀티체크 후 [선택] (검색조건용). 그리드 단건 선택에는 false
 * @param {string} [prefsUserId] - buildPrefsUserId — 팝업 크기 사용자별 기억
 * @param {string} [sizeKey='codeLookup'] - 기억 키 (예: codeLookup-balance)
 * @param {boolean} [enableSearch]
 * @param {{
 *   mode?: 'split'|'unified',
 *   keywordLabel?: string,
 *   keywordFields?: string[],
 *   codeFields?: string[], nameFields?: string[],
 *   codeLabel?: string, nameLabel?: string,
 *   hintCountLabel?: string,
 * }} [searchConfig]
 */
const CodeLookupDialog = ({
  open,
  title = '코드 조회',
  columns = [],
  rows = [],
  tabs,
  enableSearch = false,
  searchConfig = {},
  multiSelect = false,
  /** buildPrefsUserId — 있으면 팝업 크기를 사용자별로 기억 */
  prefsUserId = '',
  /** 기억 키 (기본 codeLookup). 현액 등 구분 시 codeLookup-balance */
  sizeKey = 'codeLookup',
  /** 탭이 있을 때 초기 활성 탭 id (없으면 tabs[0]) */
  defaultTabId = '',
  dialogWidth = '780px',
  dialogHeight = '560px',
  onSelect,
  onClose,
}) => {
  const gridRef = useRef(null);
  const [selected, setSelected] = useState(null);
  const hasTabs = Array.isArray(tabs) && tabs.length > 0;
  const [activeTabId, setActiveTabId] = useState('');
  const [codeQ, setCodeQ] = useState('');
  const [nameQ, setNameQ] = useState('');
  const [keywordQ, setKeywordQ] = useState('');
  const [applied, setApplied] = useState({ code: '', name: '', keyword: '' });

  const searchMode = searchConfig.mode === 'unified' ? 'unified' : 'split';
  const codeFields = searchConfig.codeFields || ['code', 'subjectDispCode', 'subjectCode', 'fundCode', 'pkgCode'];
  const nameFields = searchConfig.nameFields || ['name', 'subjectName', 'fundName', 'pkgName'];
  const keywordFields =
    searchConfig.keywordFields ||
    [...new Set([...codeFields, ...nameFields])];
  const codeLabel = searchConfig.codeLabel || '코드';
  const nameLabel = searchConfig.nameLabel || '명칭';
  const keywordLabel = searchConfig.keywordLabel || '검색';
  const hintCountLabel = searchConfig.hintCountLabel || '조회';

  useEffect(() => {
    if (!open) return;
    setSelected(null);
    setCodeQ('');
    setNameQ('');
    setKeywordQ('');
    setApplied({ code: '', name: '', keyword: '' });
    if (hasTabs) {
      const preferred =
        (defaultTabId && tabs.find((t) => t.id === defaultTabId)?.id) || tabs?.[0]?.id || '';
      setActiveTabId(preferred);
    } else setActiveTabId('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const activeTab = useMemo(() => {
    if (!hasTabs) return null;
    return tabs.find((t) => t.id === activeTabId) || tabs[0];
  }, [hasTabs, tabs, activeTabId]);

  const baseRows = activeTab ? activeTab.rows || [] : rows || [];
  const viewColumns = activeTab?.columns?.length ? activeTab.columns : columns;
  const viewHint = multiSelect
    ? activeTab?.hint ||
      '체크한 행을 [선택(체크)]로 반영합니다. [조건 해제]는 전체 조회입니다. 더블클릭은 1건만 반영합니다.'
    : activeTab?.hint ||
      (hasTabs
        ? '탭을 선택한 뒤 행 더블클릭 또는 [선택]으로 반영합니다.'
        : '행 더블클릭 또는 [선택]으로 반영합니다.');

  const filteredRows = useMemo(() => {
    if (!enableSearch) return baseRows;
    if (searchMode === 'unified') {
      const kw = applied.keyword;
      if (!kw) return baseRows;
      return (baseRows || []).filter((r) =>
        keywordFields.some((f) => includesIgnoreCase(r[f], kw))
      );
    }
    const { code, name } = applied;
    if (!code && !name) return baseRows;
    return (baseRows || []).filter((r) => {
      const codeOk = !code || codeFields.some((f) => includesIgnoreCase(r[f], code));
      const nameOk = !name || nameFields.some((f) => includesIgnoreCase(r[f], name));
      return codeOk && nameOk;
    });
  }, [enableSearch, searchMode, baseRows, applied, codeFields, nameFields, keywordFields]);

  const grid = useHiGridHook({
    gridRef,
    autoSearch: false,
    enableContextMenu: false,
    confirmCheck: false,
  });

  useEffect(() => {
    if (!open) return undefined;
    const t = setTimeout(() => {
      gridRef.current?.setData?.(filteredRows);
      gridRef.current?.clearRowChecks?.();
      setSelected(null);
    }, 0);
    return () => clearTimeout(t);
  }, [open, activeTabId, filteredRows]);

  const commitRows = useCallback(
    (list) => {
      if (!list?.length) return;
      const payload = multiSelect ? list : list[0];
      const result = onSelect?.(payload);
      const finish = (ok) => {
        if (ok === false) return;
        onClose?.();
      };
      if (result != null && typeof result.then === 'function') {
        result.then(finish).catch(() => {});
      } else {
        finish(result);
      }
    },
    [multiSelect, onSelect, onClose]
  );

  useEffect(() => {
    grid.onRowClicked = ({ data }) => setSelected(data);
    grid.onSelectRow = (data) => setSelected(data);
    grid.onGridCellDoubleClicked = (data) => {
      if (!data) return;
      // 더블클릭은 항상 해당 1건만 (빠른 단건 선택)
      commitRows([withLookupTab(data, hasTabs, activeTab?.id)]);
    };
  }, [grid, commitRows, hasTabs, activeTab?.id]);

  const handleSearch = useCallback(() => {
    if (searchMode === 'unified') {
      setApplied({ code: '', name: '', keyword: keywordQ.trim() });
    } else {
      setApplied({ code: codeQ.trim(), name: nameQ.trim(), keyword: '' });
    }
  }, [searchMode, keywordQ, codeQ, nameQ]);

  const handleOk = useCallback(() => {
    if (multiSelect) {
      const checked = gridRef.current?.getCheckedData?.() || [];
      if (checked.length) {
        commitRows(checked.map((r) => withLookupTab(r, hasTabs, activeTab?.id)));
        return;
      }
    }
    if (!selected) return;
    commitRows([withLookupTab(selected, hasTabs, activeTab?.id)]);
  }, [multiSelect, selected, commitRows, hasTabs, activeTab?.id]);

  const handleClearFilter = useCallback(() => {
    // 빈 배열 → 검색조건 해제(전체 조회)
    onSelect?.([]);
    onClose?.();
  }, [onSelect, onClose]);

  const buttons = useMemo(
    () => [
      {
        id: 'ok',
        label: multiSelect ? '선택(체크)' : '선택',
        icon: '✅',
        onClick: handleOk,
        variant: 'success',
        // 멀티: 체크 우선 · 없으면 포커스 행 — 클릭 시점에 판별
        disabled: multiSelect ? false : !selected,
      },
      ...(multiSelect
        ? [
            {
              id: 'clear',
              label: '조건 해제',
              icon: '↺',
              onClick: handleClearFilter,
              title: '선택 조건을 비우고 전체 조회합니다.',
            },
          ]
        : []),
      { id: 'close', label: '닫기', icon: '✖', onClick: onClose },
    ],
    [handleOk, handleClearFilter, onClose, multiSelect, selected]
  );

  const searchButtons = useMemo(
    () => [
      {
        id: 'search',
        label: '조회',
        icon: '🔍',
        onClick: handleSearch,
      },
    ],
    [handleSearch]
  );

  const cols = useMemo(() => {
    if (viewColumns?.length) return viewColumns;
    return [
      hiCol.roTextColumn({ header: '코드', field: 'code', size: 120 }),
      hiCol.roTextColumn({ header: '명칭', field: 'name', size: 200 }),
    ];
  }, [viewColumns]);

  return (
    <DraggableDialog
      open={open}
      title={title}
      onClose={onClose}
      dialogWidth={dialogWidth}
      dialogHeight={dialogHeight}
      prefsUserId={prefsUserId}
      sizeKey={sizeKey}
      keepMounted={false}
    >
      <Body>
        {hasTabs && (
          <TabBar>
            {tabs.map((t) => (
              <TabBtn
                key={t.id}
                type="button"
                $active={activeTab?.id === t.id}
                onClick={() => setActiveTabId(t.id)}
              >
                {t.label}
              </TabBtn>
            ))}
          </TabBar>
        )}
        {enableSearch && (
          <SearchBar>
            {searchMode === 'unified' ? (
              <SearchField>
                {keywordLabel}
                <SearchInput
                  $width="280px"
                  value={keywordQ}
                  onChange={(e) => setKeywordQ(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleSearch();
                    }
                  }}
                  placeholder="코드·명칭 포함 검색"
                />
              </SearchField>
            ) : (
              <>
                <SearchField>
                  {codeLabel}
                  <SearchInput
                    $width="130px"
                    value={codeQ}
                    onChange={(e) => setCodeQ(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleSearch();
                      }
                    }}
                    placeholder="코드 포함"
                  />
                </SearchField>
                <SearchField>
                  {nameLabel}
                  <SearchInput
                    $width="160px"
                    value={nameQ}
                    onChange={(e) => setNameQ(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleSearch();
                      }
                    }}
                    placeholder="명칭 포함"
                  />
                </SearchField>
              </>
            )}
            <ActionButtonGroup buttons={searchButtons} />
          </SearchBar>
        )}
        <Hint>
          {viewHint}
          {enableSearch ? ` · ${hintCountLabel} ${filteredRows.length}건` : ''}
          {multiSelect ? ' · 체크 후 [선택(체크)] · [조건 해제]=전체' : ''}
        </Hint>
        <ActionButtonGroup buttons={buttons} />
        <GridWrap>
          <HiGridComponent
            ref={gridRef}
            columns={cols}
            height="100%"
            showRowNumber
            rowNumberSize={56}
            showModColumn={false}
            enableRowCheck={!!multiSelect}
            enableColumnFilters
            enableHeaderMenu
            enableFreeze
            gridId={`code-lookup-${title}-${activeTab?.id || 'main'}`}
            colOptions={grid.colOptions}
          />
        </GridWrap>
      </Body>
    </DraggableDialog>
  );
};

export default CodeLookupDialog;
